import { getApplicationEndpoint } from '@gitbook/env';
import { createPalette, type PaletteCommandDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { clipboard } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { type NavigationTarget, useNavigation } from '../../navigation';
import { toHtmlLinkDocument } from './toHtmlLinkDocument';
import { toMarkdownLink } from './toMarkdownLink';

/**
 * Returns a function to create copy commands
 */
export function useCopyCommands(): (props: {
    target: NavigationTarget;
    id: string;
    title: string;
    onCopyLink?: () => void;
}) => PaletteCommandDef {
    const { getNavigationURL } = useNavigation();

    return useEventCallback((props) => {
        const url = `${getApplicationEndpoint()}${getNavigationURL(props.target)}`;
        return {
            key: 'changeRequest.copy',
            icon: Icon.Copy,
            title: 'Copy',
            descendant: () => {
                return createPalette({
                    static: true,
                    groups: [
                        {
                            key: 'changeRequest.copy',
                            commands: [
                                {
                                    key: 'changeRequest.copyLink',
                                    icon: Icon.Link,
                                    title: 'Copy link',
                                    trigger: () => {
                                        props.onCopyLink?.();
                                        clipboard.writeText(url, {
                                            notify: { title: 'URL copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'changeRequest.copyId',
                                    icon: Icon.ID,
                                    title: 'Copy ID',
                                    trigger: () => {
                                        clipboard.writeText(props.id, {
                                            notify: { title: 'ID copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'changeRequest.copyTitle',
                                    icon: Icon.Type,
                                    title: 'Copy title',
                                    trigger: () => {
                                        clipboard.writeText(props.title, {
                                            notify: { title: 'Title copied in clipboard' },
                                        });
                                    },
                                },
                                {
                                    key: 'changeRequest.copyTitleAsLink',
                                    icon: Icon.Type,
                                    title: 'Copy title as link',
                                    trigger: () => {
                                        clipboard.writeData(
                                            {
                                                'text/plain': toMarkdownLink(props.title, url),
                                                'text/html': toHtmlLinkDocument(props.title, url),
                                            },
                                            {
                                                notify: { title: 'Title copied in clipboard' },
                                            }
                                        );
                                    },
                                },
                            ],
                        },
                    ],
                });
            },
        };
    });
}
